import {useRef, useState} from 'react';
import AppFileInputButton from '../../../../components/common/AppFileInputButton';
import {SVG} from '../../../../constants/assetLists';
import type {FormQuestion} from '../../../../services/operations/forms';
import {RangeFormType, RangeType} from '../../../../services/operations/forms';
import AppTextInput from '../../../../components/common/AppTextInput';
import AppRangeSlider from '../../../../components/common/AppRangeSlider';
import AppSingleSlider from '../../../../components/common/AppSingleSlider';
import FilePreview from '../../../forms/components/AddEditForm/components/QuestionsTab/FilePreview';

export type RangeSingleAnswer = {value: number | string; image?: File};
export type RangeDualAnswer = {from: number; to: number; image?: File};
export type RangeAnswer = RangeSingleAnswer | RangeDualAnswer;

type Props = {
  question: FormQuestion;
  value: RangeAnswer | undefined;
  onChange: (val: RangeAnswer) => void;
};

/** Min/max shape from `FormQuestion.rangeConfig` or response metadata `qRange`. */
export type RangeBoundsConfig = {min?: number; max?: number} | undefined;

// ---- Textual label pairs ----
const TEXTUAL_PAIRS: Record<string, [string, string]> = {
  low: ['נמוך', 'גבוה'],
  short: ['קצר', 'ארוך'],
  cold: ['קר', 'חם'],
};

export function getTextualLabels(
  minLabel?: string,
  maxLabel?: string,
): [string, string] {
  const pair = minLabel ? TEXTUAL_PAIRS[minLabel] : undefined;
  if (pair) return pair;
  return [minLabel || 'נמוך', maxLabel || 'גבוה'];
}

export function getNumericRangeBounds(
  cfg: RangeBoundsConfig,
): [number, number] {
  const numericSafeMin = Number.isFinite(cfg?.min) ? Number(cfg!.min) : 0;
  const numericSafeMax =
    Number.isFinite(cfg?.max) && Number(cfg!.max) > numericSafeMin
      ? Number(cfg!.max)
      : numericSafeMin + 100;
  return [numericSafeMin, numericSafeMax];
}

export function formatRangeEndpointLabel(
  v: number,
  isTemperature: boolean,
): string {
  return isTemperature ? `${v} °C` : String(v);
}

export function clampInRange(n: number, lo: number, hi: number): number {
  return Math.min(Math.max(n, lo), hi);
}

function PublicRange({question, value, onChange}: Props) {
  const cfg = question.rangeConfig;

  const rangeType: RangeType = cfg?.rangeType ?? RangeType.NUMERIC;
  const formType: RangeFormType = cfg?.form ?? RangeFormType.BETWEEN;
  const canUploadImage = cfg?.canUploadImage ?? false;

  const isNumericLike =
    rangeType === RangeType.NUMERIC || rangeType === RangeType.TEMPERATURE;
  const isTemperature = rangeType === RangeType.TEMPERATURE;
  const isTextual = rangeType === RangeType.TEXTUAL;
  const isDualSlider = formType === RangeFormType.RANGE;

  const [numericSafeMin, numericSafeMax] = getNumericRangeBounds(cfg);

  const [fromTextualLabels, toTextualLabels] = getTextualLabels(
    cfg?.minLabel,
    cfg?.maxLabel,
  );

  // ---- Derive current slider values from props ----
  const currentImage = value && 'image' in value ? value.image : undefined;

  const currentSingle: number = (() => {
    if (!value) return Math.round((numericSafeMin + numericSafeMax) / 2);
    if ('value' in value) {
      return isTextual ? 50 : Number(value.value);
    }
    return numericSafeMin;
  })();

  const currentDual: [number, number] = (() => {
    if (value && 'from' in value) return [value.from, value.to];
    return [numericSafeMin, numericSafeMax];
  })();

  // ---- Local draft for dual slider (commit on change) ----
  const [dualDraft, setDualDraft] = useState<[number, number]>(currentDual);

  const sectionRef = useRef<HTMLDivElement>(null);

  const formatLabel = (v: number) => formatRangeEndpointLabel(v, isTemperature);

  const mergeAndEmit = (
    patch: Partial<RangeSingleAnswer> | Partial<RangeDualAnswer>,
  ) => {
    const next = {...(value ?? {}), ...patch} as RangeAnswer;
    onChange(next);
  };

  // ---- Single value handlers ----
  const handleSingleSlider = (_: Event, val: number | number[]) => {
    const v = val as number;
    mergeAndEmit({value: v});
  };

  const handleSingleInput = (raw: string) => {
    if (raw === '') {
      mergeAndEmit({value: '' as any});
      return;
    }
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    mergeAndEmit({value: v});
  };

  // ---- Dual value handlers ----
  const handleDualSlider = (_: Event, val: number | number[]) => {
    const [a, b] = val as number[];
    const sorted: [number, number] = [Math.min(a, b), Math.max(a, b)];
    setDualDraft(sorted);
    mergeAndEmit({from: sorted[0], to: sorted[1]});
  };

  const handleDualMinInput = (raw: string) => {
    const v = raw === '' ? numericSafeMin : Number(raw);
    if (!Number.isFinite(v)) return;
    const next: [number, number] = [v, dualDraft[1]];
    setDualDraft(next);
    mergeAndEmit({from: next[0], to: next[1]});
  };

  const handleDualMaxInput = (raw: string) => {
    const v = raw === '' ? numericSafeMax : Number(raw);
    if (!Number.isFinite(v)) return;
    const next: [number, number] = [dualDraft[0], v];
    setDualDraft(next);
    mergeAndEmit({from: next[0], to: next[1]});
  };

  // ---- Image handler ----
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    mergeAndEmit({image: file});
    requestAnimationFrame(() =>
      sectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'}),
    );
  };

  const handleImageRemove = () => {
    mergeAndEmit({image: undefined});
  };

  const qid = question._id;

  return (
    <div ref={sectionRef} className="mt-3">
      {/* ---- DUAL SLIDER (form: range) ---- */}
      {isDualSlider && isNumericLike && (
        <div className="mt-5 flex">
          <div className="w-[120px]" />

          <div className="flex-1 flex justify-center">
            <AppRangeSlider
              value={dualDraft}
              onChange={handleDualSlider}
              valueLabelDisplay="on"
              disableSwap
              min={numericSafeMin}
              max={numericSafeMax}
              valueLabelFormat={
                isTemperature
                  ? v => formatRangeEndpointLabel(v, true)
                  : undefined
              }
              startLabel={formatLabel(numericSafeMin)}
              endLabel={formatLabel(numericSafeMax)}
              containerClassName="flex-1 max-w-[707px]"
            />
          </div>
        </div>
      )}

      {/* ---- SINGLE SLIDER ---- */}
      {!isDualSlider && isNumericLike && (
        <div className="flex flex-col sm:flex-row gap-5 sm:!gap-0">
          <div className="flex items-center gap-2 w-[120px]">
            <label className="text-textColor whitespace-nowrap">
              {isTemperature ? `ערך °C` : `ערך`}
            </label>
            <AppTextInput
              type="number"
              className="border-none"
              value={value && 'value' in value ? String(value.value) : ''}
              min={numericSafeMin}
              max={numericSafeMax}
              onChange={e => handleSingleInput(e.target.value)}
            />
          </div>

          <div className="flex-1 flex justify-center">
            <AppSingleSlider
              value={
                Number.isFinite(currentSingle)
                  ? clampInRange(currentSingle, numericSafeMin, numericSafeMax)
                  : numericSafeMin
              }
              onChange={handleSingleSlider}
              valueLabelDisplay="on"
              min={numericSafeMin}
              max={numericSafeMax}
              valueLabelFormat={
                isTemperature
                  ? v => formatRangeEndpointLabel(v, true)
                  : undefined
              }
              startLabel={formatLabel(numericSafeMin)}
              endLabel={formatLabel(numericSafeMax)}
              containerClassName="w-full max-w-[586px]"
            />
          </div>
        </div>
      )}

      {/* ---- TEXTUAL SLIDER (no value input) ---- */}
      {isTextual && (
        <div className="flex">
          <div className="w-[120px]" />

          <div className="flex-1 flex justify-center">
            <AppSingleSlider
              value={value && 'value' in value ? Number(value.value) : 50}
              onChange={(_e, val) => mergeAndEmit({value: val as number})}
              min={0}
              max={100}
              valueLabelDisplay="off"
              startLabel={fromTextualLabels}
              endLabel={toTextualLabels}
              containerClassName="w-full max-w-[586px]"
            />
          </div>
        </div>
      )}

      {/* Min / Max inputs for slider */}
      {isDualSlider && (
        <div className="mt-5 flex gap-4">
          <div className="flex items-center gap-2">
            <span className=" text-textColor whitespace-nowrap">
              {isTemperature ? `בין °C` : `בין`}
            </span>
            <AppTextInput
              type="number"
              className="border-none"
              value={dualDraft[0]}
              min={numericSafeMin}
              max={dualDraft[1]}
              onChange={e => handleDualMinInput(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className=" text-textColor whitespace-nowrap">
              {isTemperature ? `ל °C` : `ל`}
            </span>
            <AppTextInput
              type="number"
              className="border-none"
              value={dualDraft[1]}
              min={dualDraft[0]}
              max={numericSafeMax}
              onChange={e => handleDualMaxInput(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* ---- Camera button (canUploadImage) ---- */}
      {canUploadImage && (
        <div className="mt-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <AppFileInputButton
              id={`public-range-image-${qid}`}
              icon={SVG.cameraIcon}
              accept="image/*"
              onChange={handleImageChange}
            />

            <span className="text-textColor">לחץ להלאת תמונה</span>
          </div>

          {currentImage && (
            <FilePreview file={currentImage} onRemove={handleImageRemove} />
          )}
        </div>
      )}
    </div>
  );
}

export default PublicRange;
